import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Code, Smartphone, Cloud, Brain, ChevronDown, Monitor, Briefcase, ArrowRight, Linkedin, Github, Twitter, Facebook, Instagram, Youtube } from 'lucide-react';
import siteLogo from '../assets/codinity.png';
import { getSettings } from '../services/api';
import { applyPageMeta } from '../utils/pageMeta';

export default function Dashboard() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const isHomeRoute = location.pathname === '/';

  // index.html carries one static title/description/canonical, so without this
  // every route reported the homepage's metadata (and a stale title lingered
  // after navigating away from the blog).
  useEffect(() => {
    applyPageMeta(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setShowServicesDropdown(false);
    setShowAboutDropdown(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const goToSection = (sectionId) => {
    setMobileMenuOpen(false);
    setShowServicesDropdown(false);
    setShowAboutDropdown(false);
    if (!isHomeRoute) {
      navigate(`/#${sectionId}`);
      return;
    }
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const serviceLinks = [
    { title: 'AI & Automation', icon: <Brain className="w-4 h-4" />, href: '/services/ai-automation' },
    { title: 'Custom Software', icon: <Code className="w-4 h-4" />, href: '/services/custom-software' },
    { title: 'Web Development', icon: <Monitor className="w-4 h-4" />, href: '/services/web-development' },
    { title: 'Mobile App Dev', icon: <Smartphone className="w-4 h-4" />, href: '/services/mobile-app-development' },
    { title: 'Cloud & Backend', icon: <Cloud className="w-4 h-4" />, href: '/services/cloud-backend' },
  ];

  const dropdownPanel = {
    background: 'rgba(2, 6, 23, 0.98)',
    border: '1px solid rgba(51, 65, 85, 0.65)',
    borderRadius: '1rem',
    boxShadow: '0 25px 60px -12px rgba(0,0,0,0.75)',
    overflow: 'hidden',
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/95 backdrop-blur-lg shadow-lg shadow-blue-500/5' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <Link to="/">
              <img src={siteLogo} alt="Codinity" style={{ height: '110px', width: 'auto' }} className="object-contain" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`transition-colors duration-300 font-medium no-underline ${isHomeRoute ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}>
                Home
              </Link>

              {/* ── SERVICES DROPDOWN ── */}
              <div
                className="relative"
                onMouseEnter={() => setShowServicesDropdown(true)}
                onMouseLeave={() => setShowServicesDropdown(false)}
              >
                <button className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium flex items-center gap-1 bg-transparent border-none cursor-pointer py-8">
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showServicesDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showServicesDropdown && (
                  <div style={{ position: 'absolute', top: '100%', paddingTop: '12px', left: '50%', transform: 'translateX(-50%)', width: '700px', zIndex: 100 }}>
                    <div style={dropdownPanel}>
                      <div style={{ display: 'flex' }}>

                        {/* LEFT: service links */}
                        <div style={{ flex: 1, padding: '1.5rem' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: '0.875rem' }}>
                            What We Build
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.125rem' }}>
                            {serviceLinks.map((svc) => (
                              <Link
                                key={svc.title}
                                to={svc.href}
                                onClick={() => setShowServicesDropdown(false)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', borderRadius: '0.5rem', textDecoration: 'none', transition: 'background 0.15s' }}
                                className="hover:bg-slate-800 group"
                              >
                                <span className="text-blue-400 group-hover:text-blue-300 transition-colors" style={{ flexShrink: 0 }}>{svc.icon}</span>
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.3 }}>{svc.title}</span>
                              </Link>
                            ))}
                          </div>

                          <div style={{ marginTop: '1rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(30,41,59,1)' }}>
                            <Link
                              to="/services"
                              onClick={() => setShowServicesDropdown(false)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', fontWeight: 600, color: '#60a5fa', textDecoration: 'none' }}
                              className="hover:text-blue-300 transition-colors"
                            >
                              View all services <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>

                        {/* RIGHT: featured card */}
                        <div style={{ width: '210px', flexShrink: 0, background: 'linear-gradient(160deg, rgba(37,99,235,0.14) 0%, rgba(147,51,234,0.12) 100%)', borderLeft: '1px solid rgba(51,65,85,0.5)', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ width: '2.5rem', height: '2.5rem', background: 'rgba(59,130,246,0.18)', borderRadius: '0.625rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', color: '#60a5fa' }}>
                              <Code className="w-5 h-5" />
                            </div>
                            <div style={{ fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', fontSize: '0.9rem', lineHeight: 1.4 }}>
                              Start Your Next Project
                            </div>
                            <p style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
                              Free consultation. Honest timelines. No fluff.
                            </p>
                          </div>
                          <Link
                            to="/contact#contact-form"
                            onClick={() => setShowServicesDropdown(false)}
                            style={{ marginTop: '1.5rem', padding: '0.55rem 1rem', background: 'rgba(59,130,246,0.85)', color: '#fff', borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center', textDecoration: 'none' }}
                            className="hover:bg-blue-500 transition-colors"
                          >
                            Get in Touch <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>

                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── ABOUT DROPDOWN ── */}
              <div
                className="relative"
                onMouseEnter={() => setShowAboutDropdown(true)}
                onMouseLeave={() => setShowAboutDropdown(false)}
              >
                <button className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium flex items-center gap-1 bg-transparent border-none cursor-pointer py-8">
                  About
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showAboutDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showAboutDropdown && (
                  <div style={{ position: 'absolute', top: '100%', paddingTop: '12px', right: 0, width: '280px', zIndex: 100 }}>
                    <div style={dropdownPanel}>
                      <div style={{ padding: '1rem' }}>

                        {/* Company */}
                        <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: '0.375rem', padding: '0 0.5rem' }}>
                          Company
                        </div>
                        {[
                          { label: 'About Codinity', sub: 'Our story and mission', section: 'about' },
                          { label: 'Industries', sub: 'Sectors we build for', section: 'work' },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { goToSection(item.section); setShowAboutDropdown(false); }}
                            style={{ display: 'flex', flexDirection: 'column', padding: '0.6rem 0.75rem', borderRadius: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background 0.15s' }}
                            className="hover:bg-slate-800 group"
                          >
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#e2e8f0', transition: 'color 0.15s' }} className="group-hover:text-blue-400">{item.label}</span>
                            <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.125rem' }}>{item.sub}</span>
                          </button>
                        ))}

                        <div style={{ height: '1px', background: 'rgba(30,41,59,1)', margin: '0.625rem 0' }} />

                        {/* Connect */}
                        <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: '0.375rem', padding: '0 0.5rem' }}>
                          Connect
                        </div>
                        <Link
                          to="/careers"
                          onClick={() => setShowAboutDropdown(false)}
                          style={{ display: 'flex', flexDirection: 'column', padding: '0.6rem 0.75rem', borderRadius: '0.5rem', textDecoration: 'none', transition: 'background 0.15s' }}
                          className="hover:bg-slate-800 group"
                        >
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#e2e8f0', transition: 'color 0.15s', display: 'flex', alignItems: 'center', gap: '0.375rem' }} className="group-hover:text-blue-400">
                            <Briefcase className="w-3.5 h-3.5" /> Careers
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.125rem' }}>Open positions</span>
                        </Link>

                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/blog"
                className={`transition-colors duration-300 font-medium no-underline ${location.pathname.startsWith('/blog') ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                Blog
              </Link>

              <Link
                to="/contact"
                className="text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium no-underline"
              >
                Contact
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-white p-2 bg-transparent border-none cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-slate-800">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={`block transition-colors duration-300 font-medium py-2 w-full text-left no-underline ${isHomeRoute ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}>Home</Link>
              <button onClick={() => goToSection('services')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">Services</button>
              <button onClick={() => goToSection('about')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">About</button>
              <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left no-underline">Careers</Link>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left no-underline">Blog</Link>
              <button onClick={() => goToSection('contact')} className="block text-slate-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2 w-full text-left bg-transparent border-none">Contact</button>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-20">
        <Outlet />
      </main>

      <footer style={{ background: '#020617', borderTop: '1px solid rgba(30,41,59,0.6)', padding: '4rem 0 2rem' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Main grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '3rem', marginBottom: '3rem' }}>

            {/* Brand */}
            <div>
              <img src={siteLogo} alt="Codinity" style={{ height: '100px', width: 'auto', marginBottom: '1rem' }} className="object-contain" />
              <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.8, maxWidth: '20rem' }}>
                Building intelligent digital solutions that drive business growth and innovation.
              </p>
            </div>

            {/* Services col 1 */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Services</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'AI & Automation',        to: '/services/ai-automation' },
                  { label: 'Custom Software',        to: '/services/custom-software' },
                  { label: 'Web Development',         to: '/services/web-development' },
                ].map(s => (
                  <li key={s.label}>
                    <Link to={s.to} style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#cbd5e1'}
                      onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services col 2 */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: 'transparent', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>_</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'Mobile App Development', to: '/services/mobile-app-development' },
                  { label: 'Cloud & Backend',        to: '/services/cloud-backend' },
                ].map(s => (
                  <li key={s.label}>
                    <Link to={s.to} style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#cbd5e1'}
                      onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Company</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {[
                  { label: 'About Us', action: () => goToSection('about') },
                  { label: 'Services', action: () => goToSection('services') },
                  { label: 'Blog',     action: () => navigate('/blog') },
                  { label: 'Careers',  action: () => navigate('/careers') },
                  { label: 'Contact',  action: () => navigate('/contact') },
                ].map(item => (
                  <li key={item.label}>
                    <button onClick={item.action}
                      style={{ fontSize: '0.875rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#cbd5e1'}
                      onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Social links */}
          {settings?.social && (
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              {[
                { key: 'linkedin',  Icon: Linkedin,  label: 'LinkedIn' },
                { key: 'github',    Icon: Github,    label: 'GitHub' },
                { key: 'twitter',   Icon: Twitter,   label: 'Twitter' },
                { key: 'facebook',  Icon: Facebook,  label: 'Facebook' },
                { key: 'instagram', Icon: Instagram, label: 'Instagram' },
                { key: 'youtube',   Icon: Youtube,   label: 'YouTube' },
              ].filter(s => settings.social[s.key]).map(({ key, Icon, label }) => (
                <a key={key} href={settings.social[key]} target="_blank" rel="noopener noreferrer" aria-label={label}
                  style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(51,65,85,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', textDecoration: 'none', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = 'rgba(51,65,85,0.4)'; }}>
                  <Icon size={14} />
                </a>
              ))}
            </div>
          )}

          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid rgba(30,41,59,0.6)', paddingTop: '1.75rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
            <p style={{ fontSize: '0.8rem', color: '#334155', margin: 0 }}>© {new Date().getFullYear()} {settings?.company?.name || 'Codinity'}. All rights reserved.</p>
          </div>

        </div>
      </footer>
    </div>
  );
}
